import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { keys } from "../../strings";

type MenuContextType = {
  showMenu: boolean;
  setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSidebarCollapsed: () => void;
  sidebarCompact: boolean;
  setSidebarCompact: (compact: boolean) => void;
};

const MenuContext = createContext<MenuContextType | null>(null);

function readSidebarCollapsed(): boolean {
  return localStorage.getItem(keys.localStorage.sidebarCollapsed) === "true";
}

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const [showMenu, setShowMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(readSidebarCollapsed);
  const [sidebarCompactOverride, setSidebarCompactOverride] = useState<boolean | null>(
    null
  );

  const setSidebarCollapsed = useCallback(
    (value: React.SetStateAction<boolean>) => {
      setSidebarCollapsedState((previous) => {
        const next = typeof value === "function" ? value(previous) : value;
        localStorage.setItem(
          keys.localStorage.sidebarCollapsed,
          next ? "true" : "false"
        );
        return next;
      });
    },
    []
  );

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((previous) => !previous);
  }, [setSidebarCollapsed]);

  const sidebarCompact =
    sidebarCompactOverride ?? sidebarCollapsed;

  const setSidebarCompact = useCallback((compact: boolean) => {
    setSidebarCompactOverride(compact);
  }, []);

  const value = useMemo(
    () => ({
      showMenu,
      setShowMenu,
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
      sidebarCompact,
      setSidebarCompact,
    }),
    [
      showMenu,
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebarCollapsed,
      sidebarCompact,
      setSidebarCompact,
    ]
  );

  return (
    <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
  );
}

export function useMenuContext() {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error("useMenuContext must be used within MenuProvider");
  }
  return context;
}
